import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { services } from "../mock";
import ServiceIcon from "../components/ServiceIcon";
import useReveal from "../hooks/useReveal";

export default function Services() {
  useReveal();
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="absolute -top-40 left-1/3 h-96 w-96 rounded-full bg-sky-500/25 blur-3xl animate-float-slow" />
        <div className="absolute -bottom-32 right-0 h-96 w-96 rounded-full bg-cyan-400/20 blur-3xl animate-float-slower" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="reveal max-w-3xl">
            <div className="inline-flex items-center gap-2 text-sky-300 font-semibold text-xs tracking-[0.25em] uppercase">
              <Sparkles className="h-3.5 w-3.5" /> Our capabilities
            </div>
            <h1 className="font-display mt-4 text-5xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
              End-to-end{" "}
              <span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">
                IT, AV & Security
              </span>{" "}
              under one roof.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Twelve deep service lines, delivered by a single team that owns
              design, deployment and lifetime support.
            </p>
          </div>

          {/* Service grid on hero */}
          <div className="reveal mt-16 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {services.map((s, i) => (
              <a
                href={`#${s.slug}`}
                key={s.slug}
                className="group flex flex-col items-center gap-2 px-3 py-4 rounded-xl bg-white/5 border border-white/10 hover:border-sky-400/60 hover:bg-white/10 transition-all"
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                <ServiceIcon name={s.icon} className="h-6 w-6 text-sky-300 group-hover:text-white transition-colors" />
                <span className="text-[11px] text-center text-slate-300 group-hover:text-white transition-colors leading-tight">
                  {s.title}
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE DETAILS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-32">
          {services.map((s, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={s.slug}
                id={s.slug}
                className="reveal grid lg:grid-cols-2 gap-12 lg:gap-16 items-center scroll-mt-28"
              >
                <div className={`relative ${reverse ? "lg:order-2" : ""}`}>
                  <div className="absolute -inset-6 bg-gradient-to-tr from-sky-100/60 to-cyan-50 rounded-3xl blur-xl" />
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-sky-500/10 ring-1 ring-slate-200 group">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4 h-11 w-11 rounded-xl bg-white/90 backdrop-blur text-sky-700 flex items-center justify-center shadow-lg">
                      <ServiceIcon name={s.icon} className="h-5 w-5" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-sky-700 font-semibold text-xs tracking-[0.25em] uppercase">
                    Service {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-slate-600 leading-relaxed text-lg">
                    {s.description}
                  </p>
                  <ul className="mt-7 grid sm:grid-cols-2 gap-3">
                    {[
                      "Design & consulting",
                      "Turnkey deployment",
                      "IT network integration",
                      "Ongoing AMC support",
                    ].map((f) => (
                      <li
                        key={f}
                        className="flex items-center gap-2.5 text-sm text-slate-700 py-2 px-3 rounded-lg bg-slate-50 border border-slate-100"
                      >
                        <CheckCircle2 className="h-4 w-4 text-sky-600 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="inline-block mt-8">
                    <Button className="group bg-sky-600 hover:bg-sky-500 text-white h-11 px-6 rounded-md shadow-md shadow-sky-500/20">
                      Request a quote
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
